import React, { useState, useEffect, useRef } from 'react';
import { Box, CustomModalLayout, Modal } from '@wix/design-system';
import { CenteredLoader } from '../centered-loader';
import s from './iframe-modal.module.scss';

export interface IframeModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title: string;
  dataHook?: string;
  loadingDelay?: number;
}

export function IframeModal({
  isOpen,
  onClose,
  url,
  title,
  dataHook = 'iframe-modal',
  loadingDelay = 1000,
}: IframeModalProps) {
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (event.data?.type === 'CLOSE_MODAL') {
        onClose();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleIframeLoad = () => {
    setTimeout(() => {
      setIsLoading(false);
    }, loadingDelay);
  };

  return (
    <Modal
      isOpen={isOpen}
      dataHook={dataHook}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={true}
    >
      <CustomModalLayout
        onCloseButtonClick={onClose}
        width="100%"
        height="100%"
        showHeaderDivider={false}
        removeContentPadding
        overflowY="hidden"
        dataHook="iframe-modal-layout"
      >
        <iframe
          ref={iframeRef}
          src={url}
          style={{
            width: '100%',
            height: '100%',
          }}
          title={title}
          data-hook="iframe-modal-iframe"
          onLoad={handleIframeLoad}
        />
        {isLoading && (
          <Box
            className={s.loadingOverlay}
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            verticalAlign="middle"
            align="center"
          >
            <CenteredLoader dataHook="iframe-modal-loader" />
          </Box>
        )}
      </CustomModalLayout>
    </Modal>
  );
}
